import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Badge,
  Button,
  Card,
  CallStateBadge,
  EmptyState,
  ErrorState,
  LiveDot,
  Loading,
  PageHeader,
  StatCard,
} from '@/components/ui';
import { formatPhone, formatRelative } from '@/lib/format';

interface LiveCall {
  id: string;
  state: string;
  direction: string;
  mode: string;
  caller_number: string | null;
  vicidial_user: string | null;
  vicidial_campaign: string | null;
  vicidial_lead_id: string | null;
  started_at: string;
  qualified: boolean;
  tenant_id: string;
  client_name: string;
  client_slug: string;
  bot_name: string | null;
  bot_code: string | null;
  integration_name: string | null;
  integration_code: string | null;
}

interface LiveCallsResponse {
  data: LiveCall[];
  count: number;
  live_on_this_node: number;
  by_client: Array<{ client: string; calls: number }>;
}

/**
 * Every call on the platform, right now.
 *
 * Polls rather than subscribing: this board is a few rows updating every few
 * seconds, and a websocket per super admin is a lot of moving parts for
 * something a 5s poll does correctly and survives a reconnect on its own.
 */
export default function LiveCalls() {
  const queryClient = useQueryClient();
  const { can } = useAuth();

  // The platform board's own route: not tenant-scoped, because this board
  // shows every client, so it is gated on a platform permission instead.
  const hangup = useMutation({
    mutationFn: (id: string) => api.post(`/admin/calls/${id}/hangup`, {}),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['admin-live-calls'] }),
  });

  const query = useQuery({
    queryKey: ['admin-live-calls'],
    queryFn: () => api.get<LiveCallsResponse>('/admin/live-calls'),
    refetchInterval: 5_000,
  });

  if (query.isPending) return <Loading label="Loading the call board…" />;
  if (query.error) return <ErrorState error={query.error} retry={() => query.refetch()} />;

  const { data: calls, count, live_on_this_node: onThisNode, by_client: byClient } = query.data;
  const talking = calls.filter((c) => c.state === 'AI_ACTIVE').length;
  const transferring = calls.filter(
    (c) => c.state === 'TRANSFERRING' || c.state === 'HUMAN_CONNECTED',
  ).length;

  return (
    <div className="w-full">
      <PageHeader
        title="Live calls"
        description="Every conversation in progress, across every client."
        actions={count > 0 ? <LiveDot label={`${count} on the phone`} /> : undefined}
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="In progress" value={count} />
        <StatCard label="Bot talking" value={talking} tone={talking ? 'good' : 'default'} />
        <StatCard label="Transferring" value={transferring} tone={transferring ? 'warn' : 'default'} />
        <StatCard
          label="On this node"
          value={onThisNode}
          sub={
            onThisNode !== count
              ? 'Differs from the database count — see below'
              : 'Matches the database'
          }
          tone={onThisNode !== count ? 'warn' : 'default'}
        />
      </div>

      {onThisNode !== count && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
          <strong className="font-semibold">Counts disagree.</strong> The database
          says {count} call{count === 1 ? '' : 's'} are live but this API process
          is holding {onThisNode}. That is normal when you run more than one API
          node. On a single node it means rows were left behind when a process
          died mid-call — the sweeper closes those about two minutes after they
          pass ten minutes old, so a gap that does not shrink is worth looking
          at rather than waiting on.
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card title="On the phone now" padded={false}>
          {calls.length === 0 ? (
            <EmptyState
              title="Nothing on the phone"
              description="Calls appear here the moment a dialer hands one to a bot."
            />
          ) : (
            <div className="scroll-x">
              <table className="glee-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Caller</th>
                    <th>Bot</th>
                    <th>Dialer</th>
                    <th>State</th>
                    <th className="text-right">Started</th>
                    <th className="text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call) => (
                    <tr key={call.id}>
                      <td>
                        <Link
                          to={`/clients/${call.tenant_id}`}
                          className="font-medium text-ink-900 hover:text-brand-600 dark:text-ink-50 dark:hover:text-brand-400"
                        >
                          {call.client_name}
                        </Link>
                      </td>
                      <td>
                        <div className="font-mono text-xs text-ink-700 dark:text-ink-200">
                          {formatPhone(call.caller_number)}
                        </div>
                        {call.vicidial_campaign && (
                          <div className="text-2xs text-ink-500">
                            {call.vicidial_campaign}
                            {call.vicidial_user && ` · ${call.vicidial_user}`}
                          </div>
                        )}
                      </td>
                      <td className="text-xs text-ink-600 dark:text-ink-300">
                        {call.bot_name ?? '—'}
                      </td>
                      <td>
                        {call.integration_name ? (
                          <Badge tone="blue">{call.integration_name}</Badge>
                        ) : (
                          <span className="text-xs text-ink-400">—</span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-1.5">
                          <CallStateBadge state={call.state} />
                          {call.qualified && <Badge tone="green">Qualified</Badge>}
                        </div>
                      </td>
                      <td className="text-right text-xs tabular-nums text-ink-500">
                        {formatRelative(call.started_at)}
                      </td>
                      <td className="text-right">
                        {can('calls:update') && (
                          <Button
                            variant="ghost"
                            loading={hangup.isPending && hangup.variables === call.id}
                            onClick={() => {
                              // Confirmed here rather than inline: a table row
                              // has nowhere to put a confirmation, and this
                              // board reaches every client's calls.
                              const ok = window.confirm(
                                `End the call with ${formatPhone(call.caller_number)} for ` +
                                  `${call.client_name}? If someone is on the line they ` +
                                  `will be hung up on.`,
                              );
                              if (ok) hangup.mutate(call.id);
                            }}
                          >
                            End call
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="By client" description="Where the load is right now.">
          {byClient.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-500">No active calls.</p>
          ) : (
            <ul className="space-y-3">
              {byClient.map((row) => {
                const share = count ? (row.calls / count) * 100 : 0;
                return (
                  <li key={row.client}>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="truncate text-sm text-ink-700 dark:text-ink-200">
                        {row.client}
                      </span>
                      <span className="shrink-0 text-sm font-semibold tabular-nums text-ink-900 dark:text-ink-50">
                        {row.calls}
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-ink-200 dark:bg-ink-800">
                      <div
                        className="h-full rounded-full bg-brand-500 transition-[width] duration-500"
                        style={{ width: `${share}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
